// ─── Decision helpers ─────────────────────────────────────────────────────────
//
// Pure, dependency-free logic for the Decision Queue. Kept separate from the
// React components so the rules (what counts as "safe to auto-approve", how
// decisions group by type, badge variants) can be unit-tested in isolation.

import type { Decision } from './api'

export type BadgeUrgency = 'critical' | 'high' | 'normal' | 'low'
export type BadgeStatus = 'success' | 'destructive' | 'warning' | 'outline'

export function urgencyVariant(urgency: string | null | undefined): BadgeUrgency {
  switch ((urgency ?? '').toUpperCase()) {
    case 'CRITICAL': return 'critical'
    case 'HIGH':     return 'high'
    case 'LOW':      return 'low'
    default:         return 'normal'
  }
}

export function statusVariant(status: string | null | undefined): BadgeStatus {
  switch ((status ?? '').toUpperCase()) {
    case 'APPROVED': return 'success'
    case 'REJECTED': return 'destructive'
    case 'EXPIRED':  return 'warning'
    default:         return 'outline'
  }
}

export function isPending(d: Decision): boolean {
  return (d.status ?? 'PENDING').toUpperCase() === 'PENDING'
}

function isElevated(d: Decision): boolean {
  const urgency = (d.urgency ?? '').toUpperCase()
  return urgency === 'CRITICAL' || urgency === 'HIGH'
}

// ─── Decision classification ────────────────────────────────────────────────
//
// Two curated decision-type sets mirror the runtime auto-approval ruleset in
// `src/config/auto-approval-rules.ts`. Keep them in lockstep with that file —
// it is the source of truth for which agent actions are low-risk. The frontend
// replicates the classification (rather than importing across the repo split)
// so the queue can group, pre-classify, and de-clutter before Mark acts.

/**
 * Low-risk content reviews the runtime already auto-approves on timeout
 * (`allow-content-review`). Treated as "safe to approve in bulk" here so the
 * dashboard's "APPROVE N SAFE" sweep is actually useful — previously it keyed
 * off `safeDefault === 'approve'`, which almost no decision sets, so it was
 * permanently stuck at zero.
 */
export const SAFE_DECISION_TYPES = new Set<string>([
  'content-plan-review',
  'social-plan-review',
  'pr-plan-review',
])

/**
 * Informational decisions that carry no real action — "no data" alerts,
 * routine health pings, analytics notes, product-intel pulses. These are
 * acknowledgements, not approvals, so they are routed OUT of the actionable
 * queue into a separate Acknowledge section rather than polluting it.
 */
export const FYI_DECISION_TYPES = new Set<string>([
  'empty-input-alert',
  'system-health',
  'agent-health',
  'analytics-anomaly',
  'qa-degradation',
  'weekly_product_pulse',
  'feature_scored',
  'review_seo_insights',
  'market_intelligence',
  'pmf_assessment',
  'pmf_milestone',
  'knowledge-stale-content',
])

/**
 * A pending decision is "safe to bulk-approve" when it is a routine, low-risk
 * content review (mirroring the runtime allow-list) OR its `safeDefault` is
 * already to approve on timeout. CRITICAL / HIGH urgency always requires a
 * deliberate human decision, and FYI items are acknowledged separately — so
 * neither is ever swept up by "approve all safe".
 */
export function isSafeToApprove(d: Decision): boolean {
  if (!isPending(d)) return false
  if (isElevated(d)) return false
  if (isFyi(d)) return false
  if (SAFE_DECISION_TYPES.has(d.decisionType)) return true
  return (d.safeDefault ?? '').trim().toLowerCase() === 'approve'
}

/**
 * A pending decision is "FYI" (informational, no action) when its type is in
 * the curated FYI set AND it is not elevated. An unexpectedly CRITICAL/HIGH
 * item of an FYI type stays in the actionable queue — we never silently shunt
 * an escalation into the quiet Acknowledge bucket.
 */
export function isFyi(d: Decision): boolean {
  if (!isPending(d)) return false
  if (isElevated(d)) return false
  return FYI_DECISION_TYPES.has(d.decisionType)
}

export function decisionTypeLabel(type: string | null | undefined): string {
  return (type ?? '').replace(/_/g, ' ').trim().toUpperCase() || 'GENERAL'
}

export interface DecisionTypeGroup {
  /** Raw decisionType key. */
  type: string
  /** Human label (uppercased, underscores → spaces). */
  label: string
  decisions: Decision[]
  /** Ids of the pending decisions in this group, in display order. */
  pendingIds: string[]
}

/**
 * Group decisions by their `decisionType` so the queue can offer batch
 * approve/reject for "decisions of the same type". Groups with outstanding
 * pending work sort first, then by pending count, then alphabetically.
 */
export function groupDecisionsByType(decisions: Decision[]): DecisionTypeGroup[] {
  const byType = new Map<string, Decision[]>()
  for (const d of decisions) {
    const key = d.decisionType || 'general'
    const arr = byType.get(key)
    if (arr) arr.push(d)
    else byType.set(key, [d])
  }

  const groups: DecisionTypeGroup[] = []
  for (const [type, arr] of byType) {
    groups.push({
      type,
      label: decisionTypeLabel(type),
      decisions: arr,
      pendingIds: arr.filter(isPending).map((d) => d.id),
    })
  }

  return groups.sort((a, b) => {
    const aHas = a.pendingIds.length > 0 ? 1 : 0
    const bHas = b.pendingIds.length > 0 ? 1 : 0
    if (aHas !== bHas) return bHas - aHas
    if (a.pendingIds.length !== b.pendingIds.length) return b.pendingIds.length - a.pendingIds.length
    return a.label.localeCompare(b.label)
  })
}

/** Flat list of pending decision ids across all groups, in display order. */
export function orderedPendingIds(groups: DecisionTypeGroup[]): string[] {
  return groups.flatMap((g) => g.pendingIds)
}

// ─── Near-identical clustering ──────────────────────────────────────────────
//
// Within a decisionType group, agents frequently queue several copies of the
// same action ("Publish the Weekly Social Calendar to social?" ×3). Rather than
// make Mark clear them one full-screen card at a time, identical decisions are
// folded into a single cluster card with a count badge that expands on demand.

/** Normalise a summary for cluster matching: lowercase, collapse whitespace,
 *  strip trailing punctuation. Conservative — only genuinely identical
 *  templates collapse together. */
function normaliseSummary(summary: string): string {
  return summary.toLowerCase().replace(/\s+/g, ' ').replace(/[\s?.!:;,]+$/g, '').trim()
}

/** Stable signature: same agent + same type + same templated summary. */
export function clusterSignature(d: Decision): string {
  return `${d.skillName} ${d.decisionType} ${normaliseSummary(d.summary ?? '')}`
}

export interface DecisionCluster {
  /** clusterSignature of the member decisions. */
  signature: string
  /** Representative summary (the first member's). */
  summary: string
  decisions: Decision[]
  pendingIds: string[]
  /** True when more than one decision shares the signature. */
  isCluster: boolean
}

/**
 * Fold a group's decisions into clusters of near-identical items, preserving
 * the input order of each cluster's first appearance. Singletons come back as
 * one-member clusters with `isCluster: false` so callers can render them as a
 * plain card.
 */
export function clusterDecisions(decisions: Decision[]): DecisionCluster[] {
  const order: string[] = []
  const bySig = new Map<string, Decision[]>()
  for (const d of decisions) {
    const sig = clusterSignature(d)
    const arr = bySig.get(sig)
    if (arr) arr.push(d)
    else {
      bySig.set(sig, [d])
      order.push(sig)
    }
  }
  return order.map((sig) => {
    const members = bySig.get(sig)!
    return {
      signature: sig,
      summary: members[0].summary,
      decisions: members,
      pendingIds: members.filter(isPending).map((d) => d.id),
      isCluster: members.length > 1,
    }
  })
}
